import React, { useEffect, useState } from "react";
import logo2 from "./logo2.png";
import "./Footer.css";
import {
  FaLinkedin,
  FaInstagram,
  FaYoutube,
  FaWhatsapp,
  FaArrowUp,
  FaPhoneAlt,
  FaEnvelope,
  FaGoogle,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { useLocation, Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import { slugify } from "./slugify";
import { reportConversion, reportConversionAndNavigate } from "./utils/googleAdsConversion";
import NewsletterSignup from "./components/NewsletterSignup";
import { openCookieSettings } from "./utils/cookieConsent";


const CATEGORY_ROUTES = {
  "smart uart display": "/smart-hmi",
  "embedded board": "/rk3566",
  "processor board": "/rk3562",
};

const Footer = () => {
  const location = useLocation();
  const [productCategories, setProductCategories] = useState([]);

  useEffect(() => {
    let isCancelled = false;

    const loadCategories = async () => {
      try {
        const categorySnap = await getDocs(collection(db, "product_categories"));

        const entries = await Promise.all(
          categorySnap.docs.map(async (categoryDoc) => {
            const title = categoryDoc.data().categoryName;
            if (!title) return null;

            let route = CATEGORY_ROUTES[title.trim().toLowerCase()];

            if (!route) {
              const subSnap = await getDocs(
                collection(db, "product_categories", categoryDoc.id, "subCategories")
              );
              const firstSub = subSnap.docs[0];
              if (firstSub) {
                const subName = firstSub.data().subCategoryName || firstSub.id;
                route = `/category/${slugify(title)}/${slugify(subName)}`;
              }
            }

            return route ? { id: categoryDoc.id, title, route } : null;
          })
        );

        if (!isCancelled) setProductCategories(entries.filter(Boolean));
      } catch (error) {
        console.error(error);
      }
    };

    loadCategories();
    return () => {
      isCancelled = true;
    };
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const openMap = () => {
    reportConversion("getDirections");
    window.open(
      "https://www.google.com/maps/place/BIBA+Electronics+Pvt+Ltd/@28.4392694,77.00067,20z/data=!4m6!3m5!1s0x390d194de9852445:0x9570adf645e056f1!8m2!3d28.4392961!4d77.000723!16s%2Fg%2F11t0pgwprw?entry=ttu",
      "_blank"
    );
  };

  const openReview = () =>
    window.open(
      "https://www.google.com/search?sxsrf=AE3TifP6U7iisJEXObfxueZOP5m0htOFEQ:1761543233638&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-E5YtqM7zCUDnSvEqIxmNorJQjm735bOyHGiAbOe-zoZvpSjdVwz5SuophF8v--ENol1n8V56MOADXtvxtCvzj7kxhVpLOoBh8geeRaC9IJ2ptaRUSQ%3D%3D&q=BIBA+Electronics+Pvt+Ltd+Reviews#lrd=0x390d194de9852445:0x9570adf645e056f1,3,,,,",
      "_blank"
    );

  return (
    <footer className="biba-footer">
      {/* ── Decorative top divider */}
      <div className="footer-divider">
        <div className="divider-line" />
        <span className="divider-leaf">◆</span>
        <div className="divider-line" />
      </div>

      {/* ── Main balanced column grid */}
      <div className="footer-inner">

        {/* Brand */}
        <div className="fcol fcol-brand">
          <img src={logo2} alt="BiBA Electronics Logo" className="f-logo" width="128" height="85" loading="lazy" decoding="async" />
          <p className="f-tagline">Made in India, for the world.</p>

          <div className="f-contact-list">
            <a
              className="f-contact-item"
              href="https://maps.google.com"
              onClick={(e) => { e.preventDefault(); openMap(); }}
            >
              <span className="f-icon map-icon"><FaMapMarkerAlt /></span>
              <span>357E, Pace City II, Sector 37, Gurugram, Haryana 122004</span>
            </a>

            <a className="f-contact-item" href="mailto:info@bibaelectronics.com">
              <span className="f-icon mail-icon"><FaEnvelope /></span>
              <span>info@bibaelectronics.com</span>
            </a>

            <a
              className="f-contact-item"
              href="https://wa.me/919667521506"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => reportConversion("whatsappClick")}
            >
              <span className="f-icon wa-icon"><FaWhatsapp /></span>
              <span>+91 96675 21506</span>
            </a>

            <a
              className="f-contact-item"
              href="tel:+919561091506"
              onClick={(e) => {
                e.preventDefault();
                reportConversionAndNavigate("callClick", "tel:+919561091506");
              }}
            >
              <span className="f-icon phone-icon"><FaPhoneAlt /></span>
              <span>+91 95610 91506</span>
            </a>
          </div>

          <button className="f-map-btn" onClick={openMap}>
            <FaMapMarkerAlt style={{ marginRight: 6 }} />
            View on Map
          </button>
        </div>

        {/* Products */}
        <div className="fcol">
          <h4 className="f-heading">Products</h4>
          <ul className="f-links">
            {productCategories.map((cat) => (
              <li key={cat.id}>
                <Link to={cat.route} onClick={scrollToTop}>{cat.title}</Link>
              </li>
            ))}
            {/* LANDING_PAGES_WIRED: static entries for the two prerendered
                product landing pages, independent of the Firestore list so
                they are present in the baked HTML of every page. */}
            <li><Link to="/hdmi-display" onClick={scrollToTop}>HDMI Displays</Link></li>
            <li><Link to="/uart-display" onClick={scrollToTop}>Smart UART Displays</Link></li>
          </ul>
        </div>

        {/* Services */}
        <div className="fcol">
          <h4 className="f-heading">Services</h4>
          <ul className="f-links">
            <li><Link to="/ems" onClick={scrollToTop}>Electronics Manufacturing</Link></li>
            <li><Link to="/embedded" onClick={scrollToTop}>Embedded Design</Link></li>
            <li><Link to="/dsync-studio-features" onClick={scrollToTop}>Dsync Studio</Link></li>
          </ul>
        </div>

        {/* About */}
        <div className="fcol fcol-about">
          <h4 className="f-heading">About</h4>
          <ul className="f-links f-links-2col">
            <li><Link to="/about/about-us" onClick={scrollToTop}>About Us</Link></li>
            <li><Link to="/about/vision-mission" onClick={scrollToTop}>Vision & Mission</Link></li>
            <li><Link to="/careers" onClick={scrollToTop}>Careers</Link></li>
            <li><Link to="/contact" onClick={scrollToTop}>Contact Us</Link></li>
            <li><Link to="/about/principles" onClick={scrollToTop}>BiBA Principles</Link></li>
            <li><Link to="/about/certifications" onClick={scrollToTop}>Certifications</Link></li>
            <li><Link to="/about/privacy-policy" onClick={scrollToTop}>Privacy Policy</Link></li>
            <li><Link to="/about/terms" onClick={scrollToTop}>Terms of Use &amp; Sale</Link></li>
            <li><Link to="/about/shipping-policy" onClick={scrollToTop}>Shipping Policy</Link></li>
            <li><Link to="/about/return-policy" onClick={scrollToTop}>Return Policy</Link></li>
            <li><Link to="/about/cancellation-policy" onClick={scrollToTop}>Cancellation Policy</Link></li>
            <li><Link to="/about/presentation" onClick={scrollToTop}>Presentation</Link></li>
          </ul>
        </div>

        {/* Connect */}
        <div className="fcol fcol-connect">
          <h4 className="f-heading">Social media</h4>



          <div className="f-social-row">
            <a href="https://www.linkedin.com/company/biba-electronics-pvt-ltd" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="f-social-btn linkedin">
              <FaLinkedin />
            </a>
            <a href="https://www.instagram.com/bibaelectronicspvtltd/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="f-social-btn instagram">
              <FaInstagram />
            </a>
            <a href="https://www.youtube.com/@BIBAElectronicsPvtLtd" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="f-social-btn youtube">
              <FaYoutube />
            </a>
            <a
              href="https://wa.me/919667521506"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              className="f-social-btn whatsapp"
              onClick={() => reportConversion("whatsappClick")}
            >
              <FaWhatsapp />
            </a>
          </div>

          <div className="f-action-btns">
            <button className="f-review-btn" onClick={openReview}>
              <FaGoogle className="g-icon" />
              <span>Leave a Google Review</span>
            </button>

            <button className="f-top-btn" onClick={scrollToTop}>
              <FaArrowUp />
              <span>Back to Top</span>
            </button>
          </div>
        </div>

      </div>

      {/* ── Newsletter strip */}
      <div className="footer-newsletter">
        <div className="footer-newsletter-inner">
          <NewsletterSignup variant="footer" source="footer" />
        </div>
      </div>

      {/* ── Bottom bar */}
      <div className="footer-bottom">
        <div className="footer-bottom-inner">
          <p className="footer-copy">
            ©2026 BiBA Electronics Pvt. Ltd. · All rights reserved.
          </p>
          <div className="footer-bottom-links">
            <Link to="/about/privacy-policy" onClick={scrollToTop}>Privacy Policy</Link>
            <span className="bottom-sep">·</span>
            <Link to="/about/cookie-policy" onClick={scrollToTop}>Cookie Policy</Link>
            <span className="bottom-sep">·</span>
            <button type="button" className="footer-cookie-btn" onClick={openCookieSettings}>Cookie Settings</button>
            <span className="bottom-sep">·</span>
            <Link to="/about/certifications" onClick={scrollToTop}>Certifications</Link>
            <span className="bottom-sep">·</span>
            <Link to="/contact" onClick={scrollToTop}>Contact Us</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
